/**
 * parseSheetData.js
 *
 * Parses local or remote data payloads used by the app.
 *
 * The app now prefers JSON files served from /public, such as:
 *   /soundData.json
 *   /pollutantData.json
 *
 * Older Google Sheets gviz text responses are still supported as a fallback.
 *
 * Usage:
 *   import { fetchSoundData, fetchPollutantData } from './Data/parseSheetData';
 *   const soundRows     = await fetchSoundData();
 *   const pollutantRows = await fetchPollutantData();
 */

/**
 * Converts a parsed payload into an array of row objects.
 * Supports:
 * - an array of objects
 * - a Google Visualization payload with table.cols/table.rows
 * - a plain object with a rows array
 *
 * @param {any} payload
 * @returns {Array<Object>}
 */
export function parseDataPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  if (payload.table?.cols && Array.isArray(payload.table.rows)) {
    return payload.table.rows.map(row => {
      const obj = {};
      payload.table.cols.forEach((col, i) => {
        obj[col.label] = row.c?.[i]?.v ?? '';
      });
      return obj;
    });
  }

  if (Array.isArray(payload.rows)) {
    return payload.rows;
  }

  return [];
}

/**
 * Parses a raw response string into an array of row objects.
 * Accepts either JSON or the older Google Visualization text wrapper format.
 *
 * @param {string} rawText
 * @returns {Array<Object>}
 */
function parseDataText(rawText) {
  const trimmed = rawText?.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return parseDataPayload(JSON.parse(trimmed));
  }

  const wrapperMatch = trimmed.match(/google\.visualization\.Query\.setResponse\((<[\s\S]*>)\)\s*;?$/);
  if (wrapperMatch) {
    return parseDataPayload(JSON.parse(wrapperMatch[1]));
  }

  const wrapperStart = trimmed.indexOf('google.visualization.Query.setResponse(');
  if (wrapperStart !== -1) {
    const jsonStr = trimmed
      .substring(wrapperStart + 'google.visualization.Query.setResponse('.length)
      .slice(0, -2)
      .trim();

    return parseDataPayload(JSON.parse(jsonStr));
  }

  return [];
}

/**
 * Fetches and parses sound data from the /public directory or Google Sheets API.
 * @returns {Promise<Array<Object>>}
 */
export async function fetchSoundData() {
  const useApi = process.env.REACT_APP_Online === 'true';
  const url = useApi
    ? (process.env.REACT_APP_SOUND_SHEET_URL || 'https://docs.google.com/spreadsheets/d/1si74K0U32TMWk0W8dDD96JYvw2cj2K1f/gviz/tq?tqx=out:json')
    : `${process.env.PUBLIC_URL}/soundData.json`;

  console.log('Fetching sound data from URL:', url);
  const res = await fetch(url);
  const contentType = res.headers.get('content-type') || '';

  if (contentType.includes('application/json') || url.endsWith('.json')) {
    const data = await res.json();
    return parseDataPayload(data);
  }

  const text = await res.text();
  return parseDataText(text);
}

/**
 * Fetches and parses pollutant data from the /public directory or Google Sheets API.
 * @returns {Promise<Array<Object>>}
 */
export async function fetchPollutantData() {
  const useApi = process.env.REACT_APP_Online === 'true';
  const url = useApi
    ? (process.env.REACT_APP_POLLUTANT_SHEET_URL || 'https://docs.google.com/spreadsheets/d/1az7_Vg0GPH2FF393w0sjCmGUxHKEYnIsSDyAJIq8fxs/gviz/tq?tqx=out:json&sheet=Sheet1')
    : `${process.env.PUBLIC_URL}/pollutantData.json`;

  console.log('Fetching pollutant data from URL:', url);
  const res = await fetch(url);
  const contentType = res.headers.get('content-type') || '';

  if (contentType.includes('application/json') || url.endsWith('.json')) {
    const data = await res.json();
    return parseDataPayload(data);
  }

  const text = await res.text();
  return parseDataText(text);
}

